import { ProfileSize } from "@/components/layout/components/account-profile";
import { AccountProfileImageContent } from "@/components/layout/components/account-profile/AccountProfileImageContent";
import { ProfileVariant } from "@/components/layout/components/account-profile/types";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import styled from "@emotion/styled";
import { mdsColors } from "@/design-system/foundations";
import { css } from "@/domains/emotion";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { MDSBadge, MDSBadgeVariant } from "@/mds";
import { PlanId } from "@/store/feature-gates/types";

interface AccountInfoProps {
  profileSize?: ProfileSize;
  withDropdownIcon?: boolean;
  profilePhotoUrl: string | null;
  profileDisplayName: string;
  profileEmailAddress?: string;
  plan?: PlanId;
}

export const SidebarAccountInfo: FC<AccountInfoProps> = observer(
  ({
    profilePhotoUrl,
    profileDisplayName,
    profileEmailAddress,
    withDropdownIcon = false,
    profileSize = ProfileSize.Standard,
    plan,
  }) => {
    return (
      <Wrapper>
        <AccountProfileImageContent
          size={profileSize}
          variant={ProfileVariant.Rounded}
          photoUrl={profilePhotoUrl}
          displayName={profileDisplayName}
          showSyncStatus
        />
        <Column>
          <AccountInfoRow>
            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
              {profileDisplayName}
            </MDSText>
            {withDropdownIcon && (
              <MdsIcon
                kind={MdsIconKind.AngleDown}
                innerStyles={{ Icon: { className: DropdownIcon } }}
              />
            )}
          </AccountInfoRow>
          <Row>
            {profileEmailAddress && (
              <AccountInfoRow>
                <StyledEmailText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.Medium}
                  color={MDSTextColor.Secondary}
                >
                  {profileEmailAddress}
                </StyledEmailText>
              </AccountInfoRow>
            )}
            {plan && (
              <MDSBadge
                label={plan === PlanId.Paid ? "Pro" : "Free"}
                variant={plan === PlanId.Paid ? MDSBadgeVariant.Emphasis : MDSBadgeVariant.Default}
                icon={plan === PlanId.Paid ? MdsIconKind.StarSolid : undefined}
              />
            )}
          </Row>
        </Column>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  width: "fit-content",
  transition: "all 0.1s ease-in-out",
}));

const AccountInfoRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const Column = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 0 0",
  marginLeft: 6,
  gap: "2px",
});

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledEmailText = styled(MDSText)`
  line-height: ${({ theme }) => theme.lineHeights.xxsmall};
`;

const DropdownIcon = css({
  color: mdsColors().grey.x500,
});
